// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, tokens, Button } from '@fluentui/react-components';
import React from 'react';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { useStreamingChat } from '../../libs/hooks/useStreamingChat';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { editConversationInput, setMessagesLoadState } from '../../redux/features/conversations/conversationsSlice';
import { AlertType } from '../../libs/models/AlertType';
import { Breakpoints, customTokens, SharedStyles } from '../../styles';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { StreamingMessageBubble } from './chat-history/StreamingMessageBubble';
import { ChatSkeleton } from './ChatSkeleton';
import { AgentProvider } from '../../libs/context/AgentContext';

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: 'calc(100vh - 64px)',
    },
    scroll: {
        ...shorthands.margin(tokens.spacingVerticalS),
        ...SharedStyles.scroll,
        overflowX: 'hidden',
    },
    history: {
        display: 'flex',
        justifyContent: 'center',
        minWidth: 0,
        overflowX: 'hidden' as const,
    },
    input: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    welcomeMessage: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        margin: '188px auto',
        color: tokens.colorNeutralForeground1,
        ...Breakpoints.small({
            margin: '24px auto',
        }),
        '& span': {
            fontSize: '32px',
        },
        '& h2': {
            marginBottom: '4px',
            fontSize: '20px',
            fontWeight: '600',
            color: customTokens.colorBlackButton,
            lineHeight: '28px',
        },
        '& p': {
            marginBottom: '24px',
            fontSize: '16px',
            fontWeight: '400',
            color: customTokens.colorGreetingText,
            lineHeight: '24px',
        },
    },
    tags: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        margin: '0 auto',
        justifyContent: 'center',
        maxWidth: '720px',
        width: '100%',
    },
    suggestionButton: {
        fontSize: '13px',
        fontWeight: '400',
        lineHeight: '18px',
        borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: customTokens.colorTags,
        color: customTokens.colorBlackButton,
        border: `1px solid transparent`,
        transition: 'border-color 0.2s ease',
        '&:hover': {
            backgroundColor: customTokens.colorTags,
            border: `1px solid ${customTokens.colorBorder}`,
        },
        '&:active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
            border: `1px solid ${customTokens.colorButtonBorder}`,
        },
    },
});

export const ChatRoom: React.FC<{
    selectedAgentId: string;
    onMessageSent?: () => void;
    isSuperAgent: boolean;
    isDocumentUploadEnabled: boolean;
}> = ({ selectedAgentId, isSuperAgent, onMessageSent, isDocumentUploadEnabled }) => {
    const classes = useClasses();
    const chat = useChat();
    const { sendStreamingMessage } = useStreamingChat();
    const dispatch = useAppDispatch();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const streaming = useAppSelector((state: RootState) => state.streaming);
    const streamState = streaming[selectedId] as typeof streaming[string] | undefined;
    const messages = conversations[selectedId].messages;
    const messagesLoadState = conversations[selectedId].messagesLoadState;

    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const lastMessageAnchorRef = React.useRef<HTMLDivElement>(null);
    // Trailing blank space that grows the scrollable area on demand. Without it, a 2nd+ message
    // can't be pinned to the vertical center the instant it's sent — there simply isn't any
    // answer content yet below it to scroll down into. See the freeze effect below.
    const bottomSpacerRef = React.useRef<HTMLDivElement>(null);
    const wasStreamingRef = React.useRef(false);
    const frozenRef = React.useRef(false);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);
    const loadingStartTimeRef = React.useRef<number | null>(null);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    React.useEffect(() => {
        // Only load if agentId is available and messages not yet loaded
        if (selectedAgentId && messagesLoadState === 'notLoaded') {
            void chat.ensureMessagesLoaded(selectedId, selectedAgentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, messagesLoadState, selectedAgentId]);

    // Timeout handler for skeleton loading
    React.useEffect(() => {
        if (messagesLoadState === 'loading') {
            const timeoutId = setTimeout(() => {
                dispatch(
                    addAlert({
                        message: 'Mesajlar yüklenemedi: Zaman aşımı. Lütfen tekrar deneyin.',
                        type: AlertType.Error,
                    }),
                );
                dispatch(
                    setMessagesLoadState({
                        chatId: selectedId,
                        state: 'notLoaded',
                    }),
                );
            }, 15000); // 15 seconds

            return () => {
                clearTimeout(timeoutId);
            };
        }
        return undefined;
    }, [messagesLoadState, selectedId, dispatch]);

    React.useEffect(() => {
        if (messagesLoadState === 'loading') {
            // Track when loading started
            if (!loadingStartTimeRef.current) {
                loadingStartTimeRef.current = Date.now();
            }

            // If no messages, show skeleton immediately (no flicker risk from welcome page)
            if (messages.length === 0) {
                setShowSkeleton(true);
            } else {
                // Delay showing skeleton by 200ms to avoid flicker on very fast loads
                const showTimer = setTimeout(() => {
                    setShowSkeleton(true);
                }, 200);

                return () => {
                    clearTimeout(showTimer);
                };
            }
        } else if (messagesLoadState === 'loaded' && showSkeleton) {
            // Loading finished. Calculate how long it took
            const loadingDuration = loadingStartTimeRef.current ? Date.now() - loadingStartTimeRef.current : 0;
            loadingStartTimeRef.current = null;

            const FAST_LOAD_THRESHOLD = 500; // ms
            const MIN_DISPLAY_TIME = 100; // ms buffer for fast loads

            if (loadingDuration < FAST_LOAD_THRESHOLD) {
                // Fast load: add minimum display time to avoid "flash" effect
                const remainingTime = Math.max(0, MIN_DISPLAY_TIME - loadingDuration);

                const hideTimer = setTimeout(() => {
                    setShowSkeleton(false);
                }, remainingTime);

                return () => {
                    clearTimeout(hideTimer);
                };
            } else {
                // Slow load: hide skeleton immediately to show loaded content
                setShowSkeleton(false);
            }
        }
        return undefined;
    }, [messagesLoadState, showSkeleton, messages.length]);

    const streamingIsStreaming = streamState?.isStreaming ?? false;

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        // While streaming, scrolling is handled by the follow-until-pinned effect below.
        if (streamingIsStreaming) return;
        // Streaming ended in frozen state — keep the pinned position. The freeze is cleared
        // when the user scrolls to the bottom or sends a new message.
        if (frozenRef.current) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll, streamingIsStreaming]);

    // Reset the "pinned" state whenever a new streaming run starts.
    React.useEffect(() => {
        if (streamingIsStreaming && !wasStreamingRef.current) {
            wasStreamingRef.current = true;
            frozenRef.current = false;
        } else if (!streamingIsStreaming) {
            wasStreamingRef.current = false;
        }
    }, [streamingIsStreaming]);

    // When a new exchange starts, immediately pin the user's question to its target line and
    // freeze the viewport there. The streaming bubble then grows downward underneath without
    // moving the page — it does NOT wait for content to grow into position.
    // - First message of the session: target is the very top (matches "fresh conversation" feel).
    // - Any later message: target is the vertical center, so the question stays in view with
    //   room below it for the growing answer (ChatGPT-style behavior), regardless of how much
    //   prior content already existed above it.
    React.useEffect(() => {
        if (!streamingIsStreaming) return;
        if (frozenRef.current) return; // already pinned — keep it fixed
        if (!shouldAutoScroll) return; // user scrolled away manually — respect it
        const container = scrollViewTargetRef.current;
        const anchor = lastMessageAnchorRef.current;
        if (!container || !anchor) return;

        // While streaming, `messages` still only contains the just-sent user message
        // (the assistant reply is added on finalize) — length 1 means this is the first exchange.
        const isFirstExchange = messages.length <= 1;
        const targetRatio = isFirstExchange ? 0 : 0.5;

        // The answer hasn't streamed in yet, so there's nothing below the question to scroll
        // into — grow a spacer first so the container has room to scroll the question down
        // to the center line.
        if (bottomSpacerRef.current) {
            bottomSpacerRef.current.style.height = isFirstExchange ? '0px' : `${container.clientHeight}px`;
        }

        const containerTop = container.getBoundingClientRect().top;
        const anchorTop = anchor.getBoundingClientRect().top;
        const targetTop = containerTop + container.clientHeight * targetRatio;

        // Instant, not smooth — an animated scroll fires intermediate scroll events that can
        // momentarily look "at bottom" and race with the freeze/unfreeze logic in the scroll
        // handler below.
        container.scrollTop += anchorTop - targetTop;
        frozenRef.current = true;
    }, [streamingIsStreaming, shouldAutoScroll, messages.length]);

    // The real answer has now taken the spacer's place — collapse it.
    React.useEffect(() => {
        if (!streamingIsStreaming && bottomSpacerRef.current) {
            bottomSpacerRef.current.style.height = '0px';
        }
    }, [streamingIsStreaming]);

    React.useEffect(() => {
        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            // User manually scrolled to the bottom — unfreeze so future auto-scroll works.
            if (isAtBottom) frozenRef.current = false;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };
    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        frozenRef.current = false;
        setShouldAutoScroll(true);

        if (options.file) {
            // File uploads use the existing synchronous flow
            await chat.getResponse(options);
        } else {
            // Text-only messages use streaming
            await sendStreamingMessage(selectedId, selectedAgentId, options.value);
        }

        if (onMessageSent) {
            onMessageSent();
        }
    };

    const handleTagClick = (question: string) => {
        dispatch(editConversationInput({ id: selectedId, newInput: question }));
    };

    return (
        <AgentProvider selectedAgentId={selectedAgentId}>
            <div className={classes.root} onDragEnter={onDragEnter} onDragOver={onDragEnter} onDragLeave={onDragLeave}>
                <div ref={scrollViewTargetRef} className={classes.scroll}>
                    <div className={classes.history}>
                        {messagesLoadState === 'loading' && (messages.length === 0 || showSkeleton) ? (
                            <ChatSkeleton />
                        ) : messages.length < 1 && conversations[selectedId].botResponseStatus === undefined ? (
                            <div className={classes.welcomeMessage}>
                                <span>👋</span>
                                <h2>Merhaba {activeUserInfo?.username ?? 'Kullanıcı'}</h2>
                                <p>KAI içerisinde Doküman yönetimi ve BT Yönetimi modülleri aktif olarak kullanılabilir. Bu modüllerle Doküman Yönetimi kapsamında Pusulamda yer alan dokümanların içeriklerine dair soru sorup soruları cevaplandırabilirsiniz. <br />BT Yönetimi kapsamında çağrılarınızı otomatik olarak açtırıp çağrınıza not eklettirip durum bilgisini öğrenebilirsiniz.</p>
                                {selectedAgentId === process.env.REACT_APP_SUPERAGENT_ID && (
                                    <div className={classes.tags}>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            className={classes.suggestionButton}
                                            onClick={() => {
                                                handleTagClick('Ana envanter sayımı nasıl yapılır?');
                                            }}
                                        >
                                            Ana envanter sayımı nasıl yapılır?
                                        </Button>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            className={classes.suggestionButton}
                                            onClick={() => {
                                                handleTagClick('Mağaza denetimleri nasıl yapılır?');
                                            }}
                                        >
                                            Mağaza denetimleri nasıl yapılır?
                                        </Button>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            className={classes.suggestionButton}
                                            onClick={() => {
                                                handleTagClick('Vize evrak talebi nasıl ilerletilmelidir?');
                                            }}
                                        >
                                            Vize evrak talebi nasıl ilerletilmelidir?
                                        </Button>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            className={classes.suggestionButton}
                                            onClick={() => {
                                                handleTagClick('Yüksek lisans eğitim desteğini kimler alabilir?');
                                            }}
                                        >
                                            Yüksek lisans eğitim desteğini kimler alabilir?
                                        </Button>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            className={classes.suggestionButton}
                                            onClick={() => {
                                                handleTagClick('Hediye alma politikasının kuralları nedir? 100 $ lık hediye geldi kabul etmeli miyim?');
                                            }}
                                        >
                                            {"Hediye alma politikasının kuralları nedir? 100 $'lık hediye geldi kabul etmeli miyim?"}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <ChatHistory
                                    messages={messages}
                                    messagesLoadState={conversations[selectedId].messagesLoadState}
                                    onRetry={() => void chat.ensureMessagesLoaded(selectedId, selectedAgentId)}
                                    lastItemRef={lastMessageAnchorRef}
                                />
                                {streamState != null && streamState.isStreaming && (
                                    <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <StreamingMessageBubble chatId={selectedId} />
                                    </div>
                                )}
                                <div ref={bottomSpacerRef} style={{ flexShrink: 0, height: 0 }} />
                            </div>
                        )}
                    </div>
                </div>
                <div className={classes.input}>
                    <ChatInput
                        isSuperAgent={isSuperAgent}
                        isDraggingOver={isDraggingOver}
                        onDragLeave={onDragLeave}
                        onSubmit={handleSubmit}
                        selectedAgentId={selectedAgentId}
                        isDocumentUploadEnabled={isDocumentUploadEnabled}
                    />
                </div>
            </div>
        </AgentProvider>
    );
};
